import { useEffect, useState, useRef } from 'react';
import api from 'config/axiosConfig';
import { useParams } from 'react-router-dom';
import SolutionCardRefactored from '../SolutionCardRefactored';
import { EntityCardRef } from 'components/Entities/EntityCardBase';
import SolutionProfileTabs from './SolutionProfileTabs';
import { Solution } from 'util/constants';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import { apiRoutes } from "util/apiRoutes";
import { applyPendingMediaOrder, syncPostCoauthors } from 'components/Posts/display/postProfileHelpers';
import { usePostProfileController } from 'components/Posts/display/usePostProfileController';
import {
  normalizeExistingCoauthors,
  usePostCoauthorState,
} from 'components/Posts/display/usePostCoauthorState';

const SolutionProfile = () => {
  const { id } = useParams<{ id: string }>();
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [referenceLink, setReferenceLink] = useState<string>('');
  const [privacy, setPrivacy] = useState<string>('public');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    entity: solution,
    setEntity: setSolution,
    error,
    isLoading,
    editMode,
    setEditMode,
    setSearchParams,
  } = usePostProfileController<Solution>({
    id,
    fallbackKind: "solution",
    getDocumentTitle: (entity) => `Solution - ${title || entity?.title || ""}`,
    notFoundError: '404: Failed to load solution profile. Please try again later.',
    genericError: 'Failed to load solution profile. Please try again later.',
  });
  const solutionCardRef = useRef<EntityCardRef>(null);
  const {
    coauthors,
    newCoauthors,
    removedCoauthors,
    initializeCoauthors,
    handleCoauthorsChange,
    resetCoauthorChanges,
  } = usePostCoauthorState(privacy);

  // Validation states
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>('');
  const [referenceLinkValidationMessage, setReferenceLinkValidationMessage] = useState<string>('');

  useEffect(() => {
    if (!solution) {
      return;
    }

    setTitle(solution.title || "");
    setDescription(solution.body || "");
    setReferenceLink(solution.url || "");
    setPrivacy(solution.properties?.privacy || 'public');
    initializeCoauthors(normalizeExistingCoauthors(solution.properties?.coauthors));
  }, [solution, initializeCoauthors]);

  const validateFields = () => {
    let isValid = true;
    
    if (!title.trim()) {
      setTitleValidationMessage('Title is required');
      isValid = false;
    } else if (title.length > 300) {
      setTitleValidationMessage('Title must be less than 300 characters');
      isValid = false;
    } else {
      setTitleValidationMessage('');
    }

    if (referenceLink && !referenceLink.trim().match(/^https?:\/\/.+/)) {
      setReferenceLinkValidationMessage('Please enter a valid URL starting with http:// or https://');
      isValid = false;
    } else {
      setReferenceLinkValidationMessage('');
    }

    return isValid;
  };

  const handleSave = async () => {
    if (isSubmitting || !validateFields()) return;

    setIsSubmitting(true);
    try {
      const mediaOrderSuccess = await applyPendingMediaOrder(solutionCardRef);
      if (!mediaOrderSuccess) {
        return;
      }

      await api.patch(apiRoutes.posts.byId(id || ""), {
        title,
        body: description,
        url: referenceLink,
        privacy,
      });
      
      await syncPostCoauthors("solution", id || "", newCoauthors, removedCoauthors, privacy);

      setSolution(prev => prev ? {
        ...prev,
        title,
        body: description,
        url: referenceLink,
        properties: {
          ...prev.properties,
          privacy,
          coauthors: [...coauthors]
        }
      } : null);
      setEditMode(false);
      resetCoauthorChanges();
      setSearchParams({});
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (solution) {
      setTitle(solution.title || "");
      setDescription(solution.body || "");
      setReferenceLink(solution.url || "");
      setPrivacy(solution.properties?.privacy || "public");
      initializeCoauthors(normalizeExistingCoauthors(solution.properties?.coauthors));
    }
    setEditMode(false);
    setSearchParams({});
  };

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find this solution." 
        errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
        buttonText="View Other Solutions" 
        buttonHref="/solutions/new" 
      /> : 
      <ServerErrorPage />;
  }

  if (isLoading || !solution) {
    return <LoadingSkeleton count={1} variant="entity-card" margin="mt-4" />;
  }

  const editorButtons = editMode ? (
    <div className="flex justify-end gap-2">
      <button
        onClick={handleCancel}
        className="btn-secondary py-1.5"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
        disabled={isSubmitting || !description}
      >
        Save
      </button>
    </div>
  ) : null;

  return (
    <div className="py-0 relative">
      <div>
        {solution && (
          <SolutionCardRefactored
            ref={solutionCardRef}
            solution={{...solution, title, body: description, url: referenceLink, properties: {...solution.properties, privacy}}}
            isEntityProfilePage={true}
            isEntitySection={false}
            truncateDescription={true}
            editMode={editMode}
            onTitleChange={(value: string) => setTitle(value)}
            onDescriptionChange={setDescription}
            onReferenceLinkChange={setReferenceLink}
            onPrivacyChange={setPrivacy}
            titleValidationMessage={titleValidationMessage}
            referenceLinkValidationMessage={referenceLinkValidationMessage}
            onCoauthorsChange={handleCoauthorsChange}
          >
            {editorButtons}
          </SolutionCardRefactored>
        )}
      </div>
      <SolutionProfileTabs solution={solution} />
    </div>
  );
};

export default SolutionProfile;
